import React from "react";
import { getJJPoints, hasFullIppon } from "../../../utils/jjCombatScoring";

const getLastName = (competiteur) =>
  String(competiteur?.nom_competiteur || competiteur?.nom || "")
    .trim()
    .toUpperCase();

const getFirstName = (competiteur) =>
  String(competiteur?.prenom_competiteur || competiteur?.prenom || "").trim();

const getClub = (competiteur) =>
  String(competiteur?.club_competiteur || competiteur?.club || "").trim();

function StatusBadge({ children, tone = "default" }) {
  const tones = {
    default: "bg-white/12 border-white/15 text-white",
    amber: "bg-amber-400 text-slate-950 border-amber-200",
    red: "bg-red-700 text-white border-red-300/40",
    emerald: "bg-emerald-400 text-slate-950 border-emerald-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-4 py-2 text-[clamp(1.6rem,1.1vw,1rem)] font-black ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function PartBox({ label, value, ippon }) {
  const highlighted = Number(ippon || 0) > 0;

  return (
    <div
      className={`rounded-2xl border px-4 py-4 h-full flex flex-col justify-center ${
        highlighted
          ? "bg-emerald-400 text-slate-950 border-emerald-200"
          : "bg-black/20 text-white border-white/15"
      }`}
    >
      <div className="text-[clamp(1.2rem,1.2vw,1rem)] uppercase font-extrabold tracking-[0.18em] opacity-85">
        {label}
      </div>

      <div className="mt-2 text-[clamp(3rem,5vw,4.8rem)] leading-none font-black tabular-nums">
        {value}
      </div>

      <div className="mt-2 text-[clamp(1.2rem,1vw,1rem)] font-bold opacity-85">
        Ippon : {ippon || 0}
      </div>
    </div>
  );
}

export default function JJCombatPublicRow({
  color = "red",
  label,
  competiteur,
  score,
  osaeSeconds = 0,
  isOsaeActive = false,
  highlightWinner = false,
}) {
  const { part1, part2, part3, total } = getJJPoints(score || {});
  const lastName = getLastName(competiteur);
  const firstName = getFirstName(competiteur);
  const club = getClub(competiteur);

  const bgClass = color === "red" ? "bg-[#d8262e]" : "bg-[#0d57d9]";
  const winnerGlow = highlightWinner ? "ring-4 ring-emerald-300/80" : "";
  const osaeProgress = Math.max(
    0,
    Math.min(100, (Number(osaeSeconds || 0) / 15) * 100)
  );

  return (
    <div
      className={`h-full w-full ${bgClass} ${winnerGlow} px-6 md:px-8 py-4 flex flex-col justify-center text-white`}
    >
      <div className="grid grid-cols-12 gap-5 items-center">
        {/* Identité */}
        <div className="col-span-4 min-w-0">
          <div className="text-[clamp(.9rem,1.1vw,1rem)] uppercase tracking-[0.28em] text-white/75 font-extrabold">
            {label}
          </div>

          <div className="mt-2 text-[clamp(2.2rem,4.4vw,4.8rem)] leading-none uppercase truncate">
            {lastName || "COMBATTANT"}
          </div>

          <div className="mt-2 text-[clamp(1.4rem,2.5vw,2.6rem)] leading-none uppercase truncate font-semibold text-white/90">
            {firstName || "—"}
          </div>

          <div className="mt-4 text-[clamp(1rem,1.8vw,1.55rem)] leading-none uppercase truncate text-white/85 font-semibold">
            {club || "Club non renseigné"}
          </div>
        </div>

        {/* Parties */}
        <div className="col-span-4 h-full">
          <div className="grid grid-cols-3 gap-3 h-full">
            <PartBox label="P1" value={part1} ippon={score?.part1Ippon || 0} />
            <PartBox label="P2" value={part2} ippon={score?.part2Ippon || 0} />
            <PartBox label="P3" value={part3} ippon={score?.part3Ippon || 0} />
          </div>
        </div>

        {/* Total */}
        <div className="col-span-1 flex flex-col items-end justify-center">
          <div className="text-[clamp(.8rem,1vw,1rem)] uppercase tracking-[0.18em] text-white/70">
            Total
          </div>
          <div className="text-[clamp(4rem,9vw,7.5rem)] leading-none tabular-nums">
            {total}
          </div>
        </div>

        {/* États / pénalités */}
        <div className="col-span-3 flex flex-col items-end justify-center">
          <div className="flex flex-wrap justify-end gap-2">
            <StatusBadge>Shido : {score?.shido || 0}</StatusBadge>
            <StatusBadge>Chui : {score?.chui || 0}</StatusBadge>

            {score?.hansokuMake && (
              <StatusBadge tone="red">Hansoku-make</StatusBadge>
            )}

            {score?.forfait && <StatusBadge tone="amber">Forfait</StatusBadge>}

            {hasFullIppon(score || {}) && (
              <StatusBadge tone="emerald">Full Ippon</StatusBadge>
            )}
          </div>
        </div>
      </div>

      {(isOsaeActive || osaeSeconds > 0) && (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-4">
            <div className="text-[clamp(.9rem,1.2vw,1rem)] uppercase tracking-[0.2em] text-white/80">
              Osae-komi
            </div>
            <div className="text-[clamp(1.8rem,3vw,2.8rem)] leading-none tabular-nums">
              {String(osaeSeconds).padStart(2, "0")}s
            </div>
          </div>

          <div className="mt-2 h-[14px] rounded-full overflow-hidden bg-black/20">
            <div
              className="h-full bg-emerald-300 transition-all duration-200"
              style={{ width: `${osaeProgress}%` }}
            />
          </div>

          <div className="mt-2 text-[clamp(.75rem,1vw,.95rem)] text-white/75 font-semibold">
            10s = Waza-ari • 15s = Ippon
          </div>
        </div>
      )}
    </div>
  );
}